import React, { useRef, useEffect } from "react";
import { GridController, GridTable } from "@ctsu/ctsu-mui-grid";
import { OpenSuccessNotification } from 'protocol-setup/components/notification';
import { getToken } from "@ctsu/loader";
import { Link, useLocation } from "react-router-dom";
import { useCurrentUser } from "protocol-setup/helpers/useIsCTSUUser";
import text from "protocol-setup/constants/text";
import { titleAndAria } from "protocol-setup/constants/errorMessages";
import NewProtocolSetupForm from "./NewProtcolSetupForm";
import { requestTypeOptions } from "../OpenRave/orHelpers";
import { requestTypeOptionsForReg } from "../Regulatory/Lovs";
import {
  formTypeOptions,
  protocolTypeOptions,
  requestStatusOptions,
  requestTypeGroupOptions
} from "./lov";

export const RequestLink = ({ requestNumber, formPartType }) => {
  return (
    <Link
      to={
        formPartType.toUpperCase() === "REGULATORY"
          ? `/protocol-setup/regulatory/${requestNumber}`
          : `/protocol-setup/open-rave/${requestNumber}`
      }
      title={titleAndAria.viewRequest(requestNumber)}
    >
      {requestNumber}
    </Link>
  );
};

export const DisplayProtocolTypeOptions = ({ protocolType }) => {
  const selectedType = protocolTypeOptions.find(
    (option) => option.value === protocolType
  );

  return <>{selectedType?.label}</>;
};

export const DisplayRequestTypeOptions = ({ requestType, formPartType }) => {
  let selectedType;
  if (formPartType.toUpperCase() === "REGULATORY") {
    selectedType = requestTypeOptionsForReg.find(
      (option) => option.value === requestType
    );
  } else {
    selectedType = requestTypeOptions.find(
      (option) => option.value.toUpperCase() === requestType
    );
  }

  return <>{selectedType?.label}</>;
};

export default function RequestBrowser({ rows, leadGroupLov, hasAddSetupPermission }) {
  const currentUser = useCurrentUser();
  const gridController = useRef(GridController.createController());
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showSuccessMessage) {
      OpenSuccessNotification({
        message: location.state.message || text.regulatory.common.successUpdate.message,
        autoHide: true
      });

      // Clear the state to prevent showing the message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);


  const columns = [
    {
      label: text.browser.requestBrowser.gridColumns.requestNumber.label,
      name: "requestNumber",
      filter: true,
      renderCell: (data) => <RequestLink {...data} />
    },
    {
      label: text.browser.requestBrowser.gridColumns.formPartType.label,
      name: "formPartType",
      filter: true,
      type: "object",
      options: formTypeOptions
    },
    {
      label: text.browser.requestBrowser.gridColumns.leadGrp.label,
      name: "leadGrp",
      filter: true,
      type: "object",
      options: leadGroupLov,
      renderCell: ({ leadGrp }) => {
        const leadGroup = leadGroupLov.find(
          (option) => option.value === leadGrp
        );
        return leadGroup ? leadGroup.label : leadGrp;
      }
    },
    {
      label: text.browser.requestBrowser.gridColumns.protocolNumber.label,
      name: "protocolNumber",
      filter: true
    },
    {
      label: text.browser.requestBrowser.gridColumns.protocolType.label,
      name: "protocolType",
      filter: true,
      type: "object",
      options: protocolTypeOptions,
      renderCell: ({ protocolType }) => (
        <DisplayProtocolTypeOptions protocolType={protocolType} />
      )
    },
    {
      label: text.browser.requestBrowser.gridColumns.requestTypeGroup.label,
      name: "requestTypeGroup",
      filter: true,
      type: "object",
      options: requestTypeGroupOptions,
      renderCell: ({ requestType, formPartType }) => (
        <DisplayRequestTypeOptions
          requestType={requestType}
          formPartType={formPartType}
        />
      )
    },
    {
      label: text.browser.requestBrowser.gridColumns.tentativeActivationDate.label,
      name: "tentativeActivationDate",
      type: "date",
      filter: false
    },
    {
      label: text.browser.requestBrowser.gridColumns.updatedDate.label,
      name: "updatedDate",
      type: "date",
      filter: false
    },
    {
      label: text.browser.requestBrowser.gridColumns.requestStatus.label,
      name: "requestStatusFormatted",
      filter: true,
      type: "text",
      visible: false    
    },
    {
      label: text.browser.requestBrowser.gridColumns.requestStatus.label,
      name: "requestStatus",
      filter: true,
      type: "object",
      options: requestStatusOptions?.filter((option => option.value !== "OPTIONAL_TRACKING")),
      renderCell: ({ requestStatus }) => {
        const status = requestStatusOptions?.find(
          (option) => option.value === requestStatus
        );
        return status ? status.label : requestStatus;
      }
    }
    
  ];

  return (
    <section className="request-browser">
      <GridTable
        id="requestNumber"
        title={text.browser.requestBrowser.tableTitle}
        renderSubtitle={() => (
          <div className="mt-1">
            {text.browser.requestBrowser.subtitle}
          </div>
        )}
        renderTitle={() => (
          <h1 className="my-0 content-page-title" aria-level="1">
            {text.browser.requestBrowser.title}
          </h1>
        )}
        columns={columns}
        rows={rows}
        toPdf={false}
        filterable
        enableInfiniteScroll={false}
        sortable
        showFilterByDefault
        enableToolbar
        highlightRow={0}
        recordPerPage={10}
        getToken={getToken}
        controller={gridController.current}
        defaultSorts={[{ field: "requestNumber", dir: "desc" }]}
        tag="request-browser"
        renderCustomHeaderIcons={() => (
          hasAddSetupPermission && <NewProtocolSetupForm leadGroupLov={leadGroupLov} />
        )}
        user={currentUser}
        exportTitle={text.browser.requestBrowser.title}
        nonExportable={["requestStatus"]}
      />
    </section>
  );
}
